import { msg, Trans } from "@lingui/macro";
import { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";
import Row from "./Row";
import { DayOfTheWeek, Hours } from "../requests/dtos/Hours";
import { useState } from "react";

type BusinessHoursDisplayProps = {
  timeZone: string,
  loadoutHours?: Hours[],
  receivingHours?: Hours[],
}

const toHoursMinutes = (openTime: string | undefined, closeTime: string | undefined) => {
  return `${openTime} - ${closeTime ?? "".padStart(8)}`;
}

const typeOfHours = (hasReceiving: boolean, receivingSelected: boolean) =>
  hasReceiving
    ? receivingSelected
      ? msg`Receiving Hours`
      : msg`Loadout Hours`
    : msg`Business Hours`;

const toTimeZone = (timeZone: string): MessageDescriptor => {
  switch (timeZone) {
    case "Central": return msg`(Central)`;
    case "Eastern": return msg`(Eastern)`;
    case "Mountain": return msg`(Mountain)`;
    case "Pacific": return msg`(Pacific)`;
    default: return msg`An unknown error occurred`;
  }
}

export const BusinessHoursDisplay = ({ loadoutHours, receivingHours, timeZone }: BusinessHoursDisplayProps) => {
  const { _ } = useLingui();
  const currentDay = Object.values(DayOfTheWeek)[new Date().getDay()];

  const hasReceiving = !!receivingHours;
  const [viewReceiving, setViewReceiving] = useState<boolean>(false);

  const hoursForDay = (dayLabel: string, day: DayOfTheWeek) => {
    const hours = viewReceiving ? receivingHours : loadoutHours;
    const dayHours = hours?.filter(h => h.day === day);

    return <Row className={currentDay === day ? "text-primary fw-bold" : ""}>
      <label className="col-auto pe-0">
        {dayLabel}:
      </label>
      <div className="col mb-0 ps-0 text-end">
        {
          (dayHours === undefined || dayHours.length <= 0)
            ? <em><Trans>Closed</Trans></em>
            : dayHours.map((dh, dhi) => <Row key={`day-${dh.day}-${dhi}`}>
              <div className="text-end">{toHoursMinutes(dh.openTime, dh.closeTime)}</div>
            </Row>)
        }
      </div>
    </Row>
  }

  return <div className="my-3">
    {
      hasReceiving &&
      <div className="text-end w-100">
        <button
          className="btn btn-sm btn-link p-0 m-0"
          onClick={() => setViewReceiving(!viewReceiving)}>
          {viewReceiving ? _(msg`View Loadout Hours`) : _(msg`View Receiving Hours`)}
        </button>
      </div>
    }
    <div className="card border" style={{maxHeight: "32vh", overflowY: "scroll", overflowX: "hidden"}}>
      <div className="card-body p-2">
        <h5>{_(typeOfHours(hasReceiving, viewReceiving)) + " " + _(toTimeZone(timeZone))}</h5>
        {hoursForDay(_(msg`Sunday`), DayOfTheWeek.Sunday)}
        {hoursForDay(_(msg`Monday`), DayOfTheWeek.Monday)}
        {hoursForDay(_(msg`Tuesday`), DayOfTheWeek.Tuesday)}
        {hoursForDay(_(msg`Wednesday`), DayOfTheWeek.Wednesday)}
        {hoursForDay(_(msg`Thursday`), DayOfTheWeek.Thursday)}
        {hoursForDay(_(msg`Friday`), DayOfTheWeek.Friday)}
        {hoursForDay(_(msg`Saturday`), DayOfTheWeek.Saturday)}
      </div>
    </div>
  </div>
}